import deepmerge from 'deepmerge';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwiperItemAttrs } from '@/components/SwiperConfigable';
import { useSelfContext, useCurrentReservesContext, useUniversityContext } from '@/context';
import ConfigurableMode from './ConfigurableMode/ConfigurableMode';
import ForgetCardModal from '@/components/modals/ForgetCardModal';
import createSwiperItem from './createSwiperItem';

export default function ReservableMode({ variant }) {

  const { university } = useUniversityContext();

  const [t] = useTranslation(['common']);
  const { findVariantById } = useSelfContext();
  const { error, items: todayReports, loading } = useCurrentReservesContext();
  const qrScannerEnabled = Boolean(university?.samad?.QRscanner);

  const [clickedItem, setClickedItem] = useState<SwiperItemAttrs>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const onActionClick = useCallback((item: SwiperItemAttrs) => {
    setModalVisible(true);
    setClickedItem(item);
  }, []);

  const items: SwiperItemAttrs[] = useMemo(() => {
    const filteredItems = todayReports
      .filter(item => item?.reserve != null)
      .filter(item => findVariantById(item?.reserve?.selfId) === variant)
      .sort((x, y) => (x?.reserve?.consumed === y?.reserve?.consumed) ? 0 : x.reserve.consumed ? 1 : -1);

    const mappedItems = [];
    for (const item of filteredItems) {
      const reserve = item?.reserve;
      if (reserve?.selectedCount === reserve?.remainedCount) {
        mappedItems.push(createSwiperItem(item, t, qrScannerEnabled));
        continue;
      }
      const consumedCount = reserve?.selectedCount - reserve?.remainedCount;
      const consumedItem = deepmerge(item, {
        reserve: {
          consumed: true,
          selectedCount: consumedCount,
          foodImageSrc: reserve?.foodImageSrc
        }
      });
      const notConsumedItem = deepmerge(item, {
        reserve: {
          consumed: false,
          selectedCount: reserve?.remainedCount,
          foodImageSrc: reserve?.foodImageSrc
        }
      });
      if (reserve?.remainedCount > 0) {
        mappedItems.push(createSwiperItem(notConsumedItem, t, qrScannerEnabled));
      }
      if (consumedCount > 0) {
        mappedItems.push(createSwiperItem(consumedItem, t, qrScannerEnabled));
      }
    }
    const sorted = mappedItems.sort((a, b) => a.disabled - b.disabled);
    return sorted;
  }, [todayReports, variant, qrScannerEnabled]);

  return (
    <>
      {modalVisible &&
        <ForgetCardModal
          variant={variant}
          item={clickedItem}
          open={modalVisible}
          dailySale={clickedItem?.data?.isDailyReserve}
          hide={() => setModalVisible(false)}
        />
      }
      <ConfigurableMode
        variant={variant}
        items={items}
        error={error}
        loading={loading}
        onActionClick={onActionClick}
      />
    </>
  );
}
